'use client';

import { c as _c } from "react/compiler-runtime";
import { segmentClassName, segmentedTrackClassName } from '@/components/ui/segmented-control';
import type { HofDashboardData } from '@/features/hof-dashboard/api/hof-dashboard-data';
import type { HofOverviewRow } from '@/features/hof-dashboard/api/hof-overview';
import { AreaView } from '@/features/hof-dashboard/components/area-view';
import { AccountButton, DashboardHeader, HofSwitch, ResponsibleLine, type Crumb } from '@/features/hof-dashboard/components/dashboard-header';
import { DashboardSkeleton, HofSkeleton, ResponsibleLineSkeleton } from '@/features/hof-dashboard/components/dashboard-skeleton';
import { DashboardTabList } from '@/features/hof-dashboard/components/dashboard-tabs';
import { AreaIcon, Panel, PRIMARY_BUTTON_CLASS, SECONDARY_BUTTON_CLASS } from '@/features/hof-dashboard/components/dashboard-ui';
import { DocumentsView } from '@/features/hof-dashboard/components/documents-view';
import { HofOverviewTable } from '@/features/hof-dashboard/components/hof-overview-table';
import { MaterialOrdersView } from '@/features/hof-dashboard/components/material-orders-view';
import { OverviewView } from '@/features/hof-dashboard/components/overview-view';
import { HOF_DASHBOARD_AREA_LABELS, HOF_DASHBOARD_AREAS, type HofDashboardArea } from '@/features/hof-dashboard/constants';
import { useRememberedHofId } from '@/features/hof-dashboard/hooks/use-remembered-hof-id';
import { useScrollToForm } from '@/features/hof-dashboard/hooks/use-scroll-to-form';
import { useTabInAddress } from '@/features/hof-dashboard/hooks/use-tab-in-address';
import { hasUnsavedWork } from '@/features/hof-dashboard/hooks/use-warn-before-leaving';
import { translate } from '@/features/hof-dashboard/texts';
import { getSubmissionProgress, isOpen, type SubmissionProgress } from '@/features/hof-dashboard/utils/submission-progress';
import { useAuthStatus } from '@/hooks/use-auth-status';
import { useCurrentLocale } from '@/hooks/use-current-locale';
import { trpc } from '@/trpc/client';
import { HitobitoNextAuthUserSchema } from '@/types/hitobito-next-auth-user';
import type { Locale } from '@/types/types';
import { i18nConfig } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { TabGroup, TabPanel, TabPanels } from '@headlessui/react';
import { FileText, LayoutGrid } from 'lucide-react';
import { signIn, useSession } from 'next-auth/react';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

/** The sections of one Hof's dashboard. */
type Tab = 'overview' | HofDashboardArea | 'documents';
const TAB_IDS: readonly Tab[] = ['overview', ...HOF_DASHBOARD_AREAS, 'documents'];

/** What a reviewer can look at: every Hof, the material orders, or one Hof's sections. */
type ReviewerScreen = 'hoefe' | 'orders' | Tab;
const REVIEWER_SCREENS: readonly ReviewerScreen[] = ['hoefe', 'orders', ...TAB_IDS];
const Message: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <Panel className="space-y-4 text-base text-green-800">{children}</Panel>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

// next-auth navigates to Cevi.DB itself. With `redirect: false` it answers `undefined` whenever
// it cannot load its providers, a second tap during the first redirect for one, so there is no
// response to read a URL from.
const signInWithCeviDatabase = async (): Promise<void> => {
  await signIn('cevi-db', {
    callbackUrl: globalThis.location.href
  });
};

/** Where every form stands today, by form id. */
const useProgress = data => {
  const $ = _c(2);
  let t0;
  bb0: {
    if (data === undefined) {
      t0 = undefined;
      break bb0;
    }
    let t1;
    if ($[0] !== data.forms) {
      const now = new Date();
      t1 = Object.fromEntries(data.forms.map(form => [form.id, getSubmissionProgress({
        mode: form.mode,
        deadline: form.deadline,
        statuses: form.entries.map(_temp),
        closed: form.closed || form.finalized
      }, now)]));
      $[0] = data.forms;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    t0 = t1;
  }
  return t0;
};
const RetryMessage: React.FC<{
  locale: Locale;
  onRetry: () => void;
}> = t0 => {
  const $ = _c(12);
  const {
    locale,
    onRetry
  } = t0;
  let t1;
  if ($[0] !== locale) {
    t1 = translate(globalThis.navigator.onLine ? "loadError" : "offline", locale);
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <p>{t1}</p>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== locale) {
    t3 = translate("retry", locale);
    $[4] = locale;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== onRetry || $[7] !== t3) {
    t4 = <button type="button" className={SECONDARY_BUTTON_CLASS} onClick={onRetry}>{t3}</button>;
    $[6] = onRetry;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = <Message>{t2}{t4}</Message>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};

/** One Hof's dashboard data, shared by its sections and the line under its name. */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- tRPC's query result
const useHofDashboard = (hofId: string, locale: Locale) => trpc.hofDashboard.getHofDashboard.useQuery({
  hofId,
  locale
}, {
  // a reviewer's answer should reach the Hof within a look away; the app's defaults
  // would keep showing what was loaded until the next full reload
  staleTime: 30_000,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
  // nothing here works offline, and yesterday's shape of it must not render after a deploy
  meta: {
    persist: false
  }
});

/** The Hof's responsible people under its name, once its dashboard has loaded. */
const HofResponsible: React.FC<{
  hofId: string;
  locale: Locale;
}> = t0 => {
  const $ = _c(5);
  const {
    hofId,
    locale
  } = t0;
  const dashboard = useHofDashboard(hofId, locale);
  if (dashboard.isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <ResponsibleLineSkeleton />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (dashboard.data === undefined) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <></>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== dashboard.data.responsible || $[3] !== locale) {
    t1 = <ResponsibleLine people={dashboard.data.responsible} locale={locale} />;
    $[2] = dashboard.data.responsible;
    $[3] = locale;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};

/** One Hof's dashboard: its sections as a row of tabs, with what is open behind each. */
const DashboardForHof: React.FC<{
  hofId: string;
  tab: Tab;
  onTab: (tab: Tab) => void;
  locale: Locale;
}> = t0 => {
  const $ = _c(65);
  const {
    hofId,
    tab,
    onTab,
    locale
  } = t0;
  const [scrollTarget, setScrollTarget] = useState();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [chosenForms, setChosenForms] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setScrollTarget(undefined);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const clearScrollTarget = t2;
  const root = useRef(null);
  useScrollToForm(root, scrollTarget, clearScrollTarget);
  const dashboard = useHofDashboard(hofId, locale);
  const progress = useProgress(dashboard.data);
  if (dashboard.isLoading) {
    let t3;
    if ($[2] !== locale) {
      t3 = <HofSkeleton locale={locale} />;
      $[2] = locale;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    return t3;
  }
  if (dashboard.data === undefined || progress === undefined) {
    let t3;
    if ($[4] !== dashboard) {
      t3 = () => void dashboard.refetch();
      $[4] = dashboard;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== locale || $[7] !== t3) {
      t4 = <RetryMessage locale={locale} onRetry={t3} />;
      $[6] = locale;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  const data = dashboard.data;
  let t3;
  if ($[9] !== onTab) {
    t3 = (area, formId) => {
      setChosenForms(chosen => ({
        ...chosen,
        [area]: formId
      }));
      onTab(area);
      setScrollTarget(formId);
    };
    $[9] = onTab;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const openForm = t3;
  let t4;
  if ($[11] !== chosenForms || $[12] !== data || $[13] !== locale || $[14] !== openForm || $[15] !== progress) {
    t4 = area_0 => <AreaView area={area_0} data={data} progress={progress} selectedFormId={chosenForms[area_0]} onSelect={formId_0 => openForm(area_0, formId_0)} locale={locale} />;
    $[11] = chosenForms;
    $[12] = data;
    $[13] = locale;
    $[14] = openForm;
    $[15] = progress;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const areaView = t4;
  let t5;
  if ($[17] !== onTab) {
    t5 = () => onTab("documents");
    $[17] = onTab;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== data || $[20] !== locale || $[21] !== onTab || $[22] !== openForm || $[23] !== progress || $[24] !== t5) {
    t6 = <OverviewView data={data} progress={progress} locale={locale} onOpen={openForm} onOpenArea={onTab} onOpenDocuments={t5} />;
    $[19] = data;
    $[20] = locale;
    $[21] = onTab;
    $[22] = openForm;
    $[23] = progress;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  let t8;
  let t9;
  if ($[26] !== areaView) {
    t7 = areaView("infrastructure");
    t8 = areaView("program");
    t9 = areaView("material");
    $[26] = areaView;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t7 = $[27];
    t8 = $[28];
    t9 = $[29];
  }
  let t10;
  if ($[30] !== data || $[31] !== locale || $[32] !== progress) {
    t10 = <DocumentsView data={data} progress={progress} locale={locale} />;
    $[30] = data;
    $[31] = locale;
    $[32] = progress;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== t10 || $[35] !== t6 || $[36] !== t7 || $[37] !== t8 || $[38] !== t9) {
    t11 = {
      overview: t6,
      infrastructure: t7,
      program: t8,
      material: t9,
      documents: t10
    };
    $[34] = t10;
    $[35] = t6;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  const panels = t11;
  let t12;
  if ($[40] !== locale) {
    t12 = id => {
      if (id === "overview") {
        return translate("tabOverview", locale);
      }
      if (id === "documents") {
        return translate("tabDocuments", locale);
      }
      return HOF_DASHBOARD_AREA_LABELS[id][locale];
    };
    $[40] = locale;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  const tabLabel = t12;
  const marker = _temp2;
  let t13;
  if ($[42] !== tab) {
    t13 = TAB_IDS.indexOf(tab);
    $[42] = tab;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== onTab) {
    t14 = index => onTab(TAB_IDS[index] ?? "overview");
    $[44] = onTab;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== data || $[47] !== locale || $[48] !== progress || $[49] !== tabLabel) {
    t15 = TAB_IDS.map(id_1 => {
      const open = data.forms.filter(form => {
        const formProgress = progress[form.id];
        return form.area === id_1 && formProgress !== undefined && isOpen(formProgress);
      }).length;
      return {
        label: tabLabel(id_1),
        marker: marker(id_1),
        open,
        openLabel: translate("openCount", locale, {
          n: open
        })
      };
    });
    $[46] = data;
    $[47] = locale;
    $[48] = progress;
    $[49] = tabLabel;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== locale) {
    t16 = translate("tabs", locale);
    $[51] = locale;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] !== t15 || $[54] !== t16) {
    t17 = <DashboardTabList tabs={t15} label={t16} />;
    $[53] = t15;
    $[54] = t16;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== panels) {
    t18 = TAB_IDS.map(id_2 => <TabPanel key={id_2} unmount={false}>{panels[id_2]}</TabPanel>);
    $[56] = panels;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] !== t18) {
    t19 = <TabPanels className="@container min-w-0">{t18}</TabPanels>;
    $[58] = t18;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] !== t13 || $[61] !== t14 || $[62] !== t17 || $[63] !== t19) {
    t20 = <div ref={root}><TabGroup selectedIndex={t13} onChange={t14} className="space-y-6">{t17}{t19}</TabGroup></div>;
    $[60] = t13;
    $[61] = t14;
    $[62] = t17;
    $[63] = t19;
    $[64] = t20;
  } else {
    t20 = $[64];
  }
  return t20;
};

/** Whether the other Hof may replace this one's dashboard, and with it what was not saved. */
const maySwitch = (locale: Locale): boolean => !hasUnsavedWork() || globalThis.confirm(translate('discardUnsaved', locale));

/** The last step of the path to a Hof: its name, or the switch where there is more than one. */
const hofCrumb = (hof: {
  id: string;
  name: string;
}, hoefe: {
  id: string;
  name: string;
}[], onSwitch: (hofId: string) => void, locale: Locale): Crumb => ({
  label: hof.name,
  control: hoefe.length > 1 ? <HofSwitch hoefe={hoefe} value={hof.id} onChange={hofId => {
    if (maySwitch(locale)) onSwitch(hofId);
  }} locale={locale} /> : undefined
});

/** The dashboard of a Hof's own people: their Hof, or a choice of theirs. */
const DashboardForAdministrator: React.FC<{
  hoefe: {
    id: string;
    name: string;
  }[];
  account: React.ReactNode;
  locale: Locale;
}> = t0 => {
  const $ = _c(27);
  const {
    hoefe,
    account,
    locale
  } = t0;
  const [tab, setTab] = useTabInAddress(TAB_IDS, "overview");
  const [selectedHofId, setSelectedHofId] = useRememberedHofId();
  let t1;
  if ($[0] !== hoefe || $[1] !== selectedHofId) {
    t1 = hoefe.find(candidate => candidate.id === selectedHofId) ?? hoefe[0];
    $[0] = hoefe;
    $[1] = selectedHofId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const hof = t1;
  if (hof === undefined) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <></>;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t2;
  if ($[4] !== hoefe || $[5] !== hof || $[6] !== locale || $[7] !== setSelectedHofId) {
    t2 = hofCrumb(hof, hoefe, setSelectedHofId, locale);
    $[4] = hoefe;
    $[5] = hof;
    $[6] = locale;
    $[7] = setSelectedHofId;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t2) {
    t3 = [t2];
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== hof.id || $[12] !== locale) {
    t4 = <HofResponsible hofId={hof.id} locale={locale} />;
    $[11] = hof.id;
    $[12] = locale;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== account || $[15] !== locale || $[16] !== t3 || $[17] !== t4) {
    t5 = <DashboardHeader crumbs={t3} subline={t4} isReviewer={false} account={account} locale={locale} />;
    $[14] = account;
    $[15] = locale;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== hof.id || $[20] !== locale || $[21] !== setTab || $[22] !== tab) {
    t6 = <DashboardForHof key={hof.id} hofId={hof.id} tab={tab} onTab={setTab} locale={locale} />;
    $[19] = hof.id;
    $[20] = locale;
    $[21] = setTab;
    $[22] = tab;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t5 || $[25] !== t6) {
    t7 = <div className="space-y-6">{t5}{t6}</div>;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
};

/** The two views of the Ressort over every Hof, as a segmented control under its header. */
const ReviewerTabs: React.FC<{
  screen: 'hoefe' | 'orders';
  onScreen: (screen: 'hoefe' | 'orders') => void;
  locale: Locale;
}> = t0 => {
  const $ = _c(11);
  const {
    screen,
    onScreen,
    locale
  } = t0;
  let t1;
  if ($[0] !== locale) {
    t1 = translate("reviewerViews", locale);
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn(segmentedTrackClassName, "w-fit max-w-full overflow-x-auto");
    t3 = ["hoefe", "orders"];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== locale || $[5] !== onScreen || $[6] !== screen) {
    t4 = (t3 as const).map(candidate => <button key={candidate} type="button" aria-current={candidate === screen ? "page" : undefined} className={cn(segmentClassName(candidate === screen), "shrink-0 px-4 text-sm font-semibold")} onClick={() => onScreen(candidate)}>{translate(candidate === "hoefe" ? "allHoefe" : "materialOrders", locale)}</button>);
    $[4] = locale;
    $[5] = onScreen;
    $[6] = screen;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t4) {
    t5 = <nav aria-label={t1} className={t2}>{t4}</nav>;
    $[8] = t1;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};

/**
 * The dashboard of the Ressorts: every Hof and the material orders, and from there one Hof's
 * dashboard, where they answer what it handed in. The address names the screen, so the back
 * button returns from a Hof to the list.
 */
const DashboardForReviewer: React.FC<{
  hoefe: {
    id: string;
    name: string;
  }[];
  rows: HofOverviewRow[];
  account: React.ReactNode;
  locale: Locale;
}> = t0 => {
  const $ = _c(60);
  const {
    hoefe,
    rows,
    account,
    locale
  } = t0;
  const [screen, setScreen] = useTabInAddress(REVIEWER_SCREENS, "hoefe");
  const [selectedHofId, setSelectedHofId] = useRememberedHofId();
  let t1;
  if ($[0] !== hoefe || $[1] !== selectedHofId) {
    t1 = hoefe.find(candidate => candidate.id === selectedHofId) ?? hoefe[0];
    $[0] = hoefe;
    $[1] = selectedHofId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const hof = t1;
  let t2;
  if ($[3] !== setScreen || $[4] !== setSelectedHofId) {
    t2 = (hofId, area) => {
      setSelectedHofId(hofId);
      setScreen(area ?? "overview");
      globalThis.scrollTo({
        top: 0
      });
    };
    $[3] = setScreen;
    $[4] = setSelectedHofId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const openHof = t2;
  let t3;
  if ($[6] !== locale || $[7] !== setScreen) {
    t3 = () => {
      if (maySwitch(locale)) {
        setScreen("hoefe");
      }
    };
    $[6] = locale;
    $[7] = setScreen;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const toList = t3;
  if (screen === "hoefe" || screen === "orders" || hof === undefined) {
    const list = screen === "orders" ? "orders" : "hoefe";
    const t4 = list === "orders" ? "materialOrders" : "allHoefe";
    let t5;
    if ($[9] !== locale || $[10] !== t4) {
      t5 = translate(t4, locale);
      $[9] = locale;
      $[10] = t4;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    const title = t5;
    let t6;
    if ($[12] !== title) {
      t6 = [{
        label: title
      }];
      $[12] = title;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== account || $[15] !== locale || $[16] !== t6) {
      t7 = <DashboardHeader crumbs={t6} isReviewer={true} account={account} locale={locale} />;
      $[14] = account;
      $[15] = locale;
      $[16] = t6;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    let t8;
    if ($[18] !== list || $[19] !== locale || $[20] !== setScreen) {
      t8 = <ReviewerTabs screen={list} onScreen={setScreen} locale={locale} />;
      $[18] = list;
      $[19] = locale;
      $[20] = setScreen;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    let t9;
    if ($[22] !== list || $[23] !== locale || $[24] !== openHof || $[25] !== rows) {
      t9 = list === "orders" ? <MaterialOrdersView onOpenHof={hofId_0 => openHof(hofId_0, "material")} locale={locale} /> : <HofOverviewTable hoefe={rows} onOpen={openHof} locale={locale} />;
      $[22] = list;
      $[23] = locale;
      $[24] = openHof;
      $[25] = rows;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    let t10;
    if ($[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
      t10 = <div className="space-y-6">{t7}{t8}{t9}</div>;
      $[27] = t7;
      $[28] = t8;
      $[29] = t9;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    return t10;
  }
  let t4;
  if ($[31] !== locale) {
    t4 = translate("allHoefe", locale);
    $[31] = locale;
    $[32] = t4;
  } else {
    t4 = $[32];
  }
  let t5;
  if ($[33] !== t4 || $[34] !== toList) {
    t5 = {
      label: t4,
      onClick: toList
    };
    $[33] = t4;
    $[34] = toList;
    $[35] = t5;
  } else {
    t5 = $[35];
  }
  let t6;
  if ($[36] !== hoefe || $[37] !== hof || $[38] !== locale || $[39] !== setSelectedHofId) {
    t6 = hofCrumb(hof, hoefe, setSelectedHofId, locale);
    $[36] = hoefe;
    $[37] = hof;
    $[38] = locale;
    $[39] = setSelectedHofId;
    $[40] = t6;
  } else {
    t6 = $[40];
  }
  let t7;
  if ($[41] !== t5 || $[42] !== t6) {
    t7 = [t5, t6];
    $[41] = t5;
    $[42] = t6;
    $[43] = t7;
  } else {
    t7 = $[43];
  }
  let t8;
  if ($[44] !== hof.id || $[45] !== locale) {
    t8 = <HofResponsible hofId={hof.id} locale={locale} />;
    $[44] = hof.id;
    $[45] = locale;
    $[46] = t8;
  } else {
    t8 = $[46];
  }
  let t9;
  if ($[47] !== account || $[48] !== locale || $[49] !== t7 || $[50] !== t8) {
    t9 = <DashboardHeader crumbs={t7} subline={t8} isReviewer={true} account={account} locale={locale} />;
    $[47] = account;
    $[48] = locale;
    $[49] = t7;
    $[50] = t8;
    $[51] = t9;
  } else {
    t9 = $[51];
  }
  let t10;
  if ($[52] !== hof.id || $[53] !== locale || $[54] !== screen || $[55] !== setScreen) {
    t10 = <DashboardForHof key={hof.id} hofId={hof.id} tab={screen} onTab={setScreen} locale={locale} />;
    $[52] = hof.id;
    $[53] = locale;
    $[54] = screen;
    $[55] = setScreen;
    $[56] = t10;
  } else {
    t10 = $[56];
  }
  let t11;
  if ($[57] !== t10 || $[58] !== t9) {
    t11 = <div className="space-y-6">{t9}{t10}</div>;
    $[57] = t10;
    $[58] = t9;
    $[59] = t11;
  } else {
    t11 = $[59];
  }
  return t11;
};
const DashboardForUser: React.FC<{
  account: React.ReactNode;
  locale: Locale;
}> = t0 => {
  const $ = _c(18);
  const {
    account,
    locale
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      refetchOnMount: true,
      meta: {
        persist: false
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const hoefe = trpc.hofDashboard.getMyHofList.useQuery(undefined, t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      staleTime: 30000,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      meta: {
        persist: false
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const overview = trpc.hofDashboard.getHofOverview.useQuery(undefined, t2);
  if (hoefe.isLoading || overview.isLoading) {
    let t3;
    if ($[2] !== locale) {
      t3 = <DashboardSkeleton locale={locale} />;
      $[2] = locale;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    return t3;
  }
  if (hoefe.data === undefined) {
    let t3;
    if ($[4] !== hoefe) {
      t3 = () => void hoefe.refetch();
      $[4] = hoefe;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== locale || $[7] !== t3) {
      t4 = <RetryMessage locale={locale} onRetry={t3} />;
      $[6] = locale;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  if (hoefe.data.length === 0) {
    let t3;
    if ($[9] !== locale) {
      t3 = translate("noAccess", locale);
      $[9] = locale;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== t3) {
      t4 = <Message><p>{t3}</p></Message>;
      $[11] = t3;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    return t4;
  }
  let t3;
  if ($[13] !== account || $[14] !== hoefe.data || $[15] !== locale || $[16] !== overview.data) {
    t3 = overview.data?.isReviewer === true ? <DashboardForReviewer hoefe={hoefe.data} rows={overview.data.hoefe} account={account} locale={locale} /> : <DashboardForAdministrator hoefe={hoefe.data} account={account} locale={locale} />;
    $[13] = account;
    $[14] = hoefe.data;
    $[15] = locale;
    $[16] = overview.data;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  return t3;
};
const HofDashboardContent: React.FC = () => {
  const $ = _c(22);
  const locale = useCurrentLocale(i18nConfig) as Locale;
  const {
    data: session
  } = useSession();
  const status = useAuthStatus();
  if (status === "loading") {
    let t0;
    if ($[0] !== locale) {
      t0 = <DashboardSkeleton locale={locale} />;
      $[0] = locale;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  if (status === "unauthenticated") {
    let t0;
    if ($[2] !== locale) {
      t0 = translate("loginRequired", locale);
      $[2] = locale;
      $[3] = t0;
    } else {
      t0 = $[3];
    }
    let t1;
    if ($[4] !== t0) {
      t1 = <p>{t0}</p>;
      $[4] = t0;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== locale) {
      t2 = translate("login", locale);
      $[6] = locale;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== t2) {
      t3 = <button type="button" className={PRIMARY_BUTTON_CLASS} onClick={_temp3}>{t2}</button>;
      $[8] = t2;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    let t4;
    if ($[10] !== t1 || $[11] !== t3) {
      t4 = <Message>{t1}{t3}</Message>;
      $[10] = t1;
      $[11] = t3;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    return t4;
  }
  let t0;
  if ($[13] !== session?.user) {
    t0 = HitobitoNextAuthUserSchema.safeParse(session?.user);
    $[13] = session?.user;
    $[14] = t0;
  } else {
    t0 = $[14];
  }
  const user = t0.data;
  const name = user?.name ?? user?.email ?? "";
  let t1;
  if ($[15] !== locale || $[16] !== name || $[17] !== user) {
    t1 = user !== undefined && name !== "" ? <AccountButton userId={user.uuid} name={name} locale={locale} /> : undefined;
    $[15] = locale;
    $[16] = name;
    $[17] = user;
    $[18] = t1;
  } else {
    t1 = $[18];
  }
  let t2;
  if ($[19] !== locale || $[20] !== t1) {
    t2 = <DashboardForUser account={t1} locale={locale} />;
    $[19] = locale;
    $[20] = t1;
    $[21] = t2;
  } else {
    t2 = $[21];
  }
  return t2;
};

/**
 * The Hof dashboard as an editor places it on a page: the address administrators of a Hof
 * hand in its plans, order material and follow what the Ressorts say; the Ressorts see every
 * Hof and answer them. Signed-out visitors are asked to sign in; nothing is fetched for them,
 * since the tRPC client answers a 401 with a sign out and a redirect.
 *
 * Laid out by container queries: the block may sit in a content column narrower than the
 * viewport, or in a two-column block.
 */
export const HofDashboard: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="@container/dashboard"><HofDashboardContent /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
function _temp(entry) {
  return entry.status;
}
function _temp2(id_0) {
  if (id_0 === "overview") {
    return <LayoutGrid className="h-4 w-4" aria-hidden={true} />;
  }
  if (id_0 === "documents") {
    return <FileText className="h-4 w-4" aria-hidden={true} />;
  }
  return <AreaIcon area={id_0} />;
}
function _temp3() {
  return void signInWithCeviDatabase();
}